import type {
  GuideOverlayState,
  GuidePrimitive,
  GuideRenderSize,
  NormalizedGuidePoint,
} from "./activityTypes";
import { MAX_TOTAL_PRIMITIVES_PER_GUIDE } from "./activityValidation";

export const GUIDE_RENDER_STYLE = Object.freeze({
  colour: "#6d4ee8",
  completedAlpha: 0.24,
  currentAlpha: 0.78,
  cardAlpha: 0.9,
  lineWidthCss: 3,
  dashCss: [9, 7] as const,
});

function mapPoint(point: NormalizedGuidePoint, size: GuideRenderSize): { x: number; y: number } {
  return { x: point.x * size.width, y: point.y * size.height };
}

function beginPrimitive(context: CanvasRenderingContext2D): void {
  context.beginPath();
}

export function drawGuidePrimitive(
  context: CanvasRenderingContext2D,
  primitive: GuidePrimitive,
  size: GuideRenderSize,
): void {
  beginPrimitive(context);
  switch (primitive.type) {
    case "line": {
      const start = mapPoint(primitive.start, size);
      const end = mapPoint(primitive.end, size);
      context.moveTo(start.x, start.y);
      context.lineTo(end.x, end.y);
      break;
    }
    case "polyline": {
      const first = mapPoint(primitive.points[0]!, size);
      context.moveTo(first.x, first.y);
      for (const point of primitive.points.slice(1)) {
        const mapped = mapPoint(point, size);
        context.lineTo(mapped.x, mapped.y);
      }
      if (primitive.closed) context.closePath();
      break;
    }
    case "rectangle": {
      const x = primitive.x * size.width;
      const y = primitive.y * size.height;
      const width = primitive.width * size.width;
      const height = primitive.height * size.height;
      if (primitive.cornerRadius) {
        context.roundRect(
          x,
          y,
          width,
          height,
          primitive.cornerRadius * Math.min(size.width, size.height),
        );
      } else {
        context.rect(x, y, width, height);
      }
      break;
    }
    case "ellipse":
      context.ellipse(
        primitive.cx * size.width,
        primitive.cy * size.height,
        primitive.rx * size.width,
        primitive.ry * size.height,
        0,
        0,
        Math.PI * 2,
      );
      break;
    case "arc":
      context.arc(
        primitive.cx * size.width,
        primitive.cy * size.height,
        primitive.radius * Math.min(size.width, size.height),
        primitive.startAngle,
        primitive.endAngle,
        primitive.anticlockwise ?? false,
      );
      break;
    case "bezier": {
      const start = mapPoint(primitive.start, size);
      const control1 = mapPoint(primitive.control1, size);
      const control2 = mapPoint(primitive.control2, size);
      const end = mapPoint(primitive.end, size);
      context.moveTo(start.x, start.y);
      context.bezierCurveTo(control1.x, control1.y, control2.x, control2.y, end.x, end.y);
      break;
    }
  }
  context.stroke();
}

function configureContext(context: CanvasRenderingContext2D, size: GuideRenderSize): void {
  const zoom = Math.max(0.05, size.zoom);
  context.strokeStyle = GUIDE_RENDER_STYLE.colour;
  context.lineWidth = GUIDE_RENDER_STYLE.lineWidthCss / zoom;
  context.lineCap = "round";
  context.lineJoin = "round";
  context.setLineDash(GUIDE_RENDER_STYLE.dashCss.map((value) => value / zoom));
}

export function renderGuideOverlay(
  context: CanvasRenderingContext2D,
  overlay: Readonly<GuideOverlayState> | null,
  size: GuideRenderSize,
): void {
  if (!overlay?.visible) return;
  const currentIndex = Math.min(
    overlay.activity.steps.length - 1,
    Math.max(0, Math.floor(overlay.currentStep) - 1),
  );
  let rendered = 0;
  context.save();
  configureContext(context, size);
  for (let index = 0; index <= currentIndex; index += 1) {
    context.globalAlpha =
      index === currentIndex ? GUIDE_RENDER_STYLE.currentAlpha : GUIDE_RENDER_STYLE.completedAlpha;
    for (const primitive of overlay.activity.steps[index]!.primitives) {
      if (rendered >= MAX_TOTAL_PRIMITIVES_PER_GUIDE) break;
      drawGuidePrimitive(context, primitive, size);
      rendered += 1;
    }
  }
  context.restore();
}

export function renderGuidePreview(
  context: CanvasRenderingContext2D,
  primitives: readonly GuidePrimitive[],
  size: GuideRenderSize,
): void {
  context.save();
  configureContext(context, size);
  context.globalAlpha = GUIDE_RENDER_STYLE.cardAlpha;
  for (const primitive of primitives) drawGuidePrimitive(context, primitive, size);
  context.restore();
}
